import { useState, useCallback } from 'react';

const ORDER_KEY = 'umax_card_order';
const COLLAPSED_KEY = 'umax_card_collapsed';

export const DEFAULT_ORDER = [
  'latest-run',
  'pace-bands',
  'next-workout',
  'recovery',
  'training-bands',
  'fuel',
  'scheduled-runs',
  'feedback',
];

// Cards that open on a first visit (and that returning users get at the top of
// a saved order rather than buried at the bottom).
export const DEFAULT_OPEN = ['latest-run'];

function loadJSON(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}

function mergeOrder(saved, defaults) {
  const known = new Set(defaults);
  const savedKnown = saved.filter((id) => known.has(id));
  // A new default-first card belongs at the front for returning users too —
  // appending it would hide the Latest run card under seven saved cards.
  const missingFront = DEFAULT_OPEN.filter((id) => known.has(id) && !savedKnown.includes(id));
  const kept = [...missingFront, ...savedKnown];
  const seen = new Set(kept);
  defaults.forEach((id) => { if (!seen.has(id)) kept.push(id); });
  return kept;
}

export default function useCardPrefs() {
  const [order, setOrderState] = useState(() => {
    const saved = loadJSON(ORDER_KEY);
    return Array.isArray(saved) ? mergeOrder(saved, DEFAULT_ORDER) : DEFAULT_ORDER;
  });

  const [collapsed, setCollapsed] = useState(() => {
    const saved = loadJSON(COLLAPSED_KEY);
    if (saved) return saved;
    const all = {};
    DEFAULT_ORDER.forEach((id) => { if (!DEFAULT_OPEN.includes(id)) all[id] = true; });
    return all;
  });

  const setOrder = useCallback((updater) => {
    setOrderState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      localStorage.setItem(ORDER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const isCollapsed = useCallback((id) => !!collapsed[id], [collapsed]);

  const toggleCollapsed = useCallback((id) => {
    setCollapsed((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id]; else next[id] = true;
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return { order, setOrder, isCollapsed, toggleCollapsed };
}
